// Ticket 302's copy, in our own words (docs/tickets/302/design.md, "Draft
// copy" + "a11y & i18n notes"), authored in nl and en. i18n.ts spreads these
// into its two dictionaries; lib/uiCopy/socialHub.ts holds the other five locales. Placeholders use translate()'s
// real `{name}` syntax (confirmed in lib/i18n.ts), not design.md's `{{name}}`
// notation -- a deliberate correction, noted here once rather than at every
// call site.
//
// Deliberate wording choices carried over from design.md (do not "simplify"
// these without re-reading the design doc's reasoning):
// - "Zichtbaar voor leden en genodigden" / "Visible to members and invitees"
//   never "public"/"private" -- there is no directory to imply.
// - The broadcast empty state is scoped to "in deze proeverij"/"in this
//   tasting", never a bare "no activity" (that would imply a browsable feed).
// - The public-toggle helper text spells out that it is not profile
//   discovery -- keep it verbatim, it is load-bearing copy, not filler.
export const socialCopy = {
  nl: {
    // About (extended) — new "Account" section
    "social.about.section.account": "Account",
    "social.about.sync.title": "Zorg dat je notities meegaan",
    "social.about.sync.action": "Inloggen",
    "social.about.sync.manage": "Beheren",
    "social.about.sync.signedInAs": "Ingelogd als {email}",
    "social.about.sync.unconfirmed":
      "We konden je account nu niet controleren; je blijft ingelogd en we proberen het straks opnieuw.",
    // SignInGate: friends, groups and sessions when signed out.
    "social.signin.gate.title": "Log in om samen te proeven",
    "social.signin.gate.body":
      "Vrienden, groepen en proeverijen horen bij een account. Je notities op dit toestel blijven van jou.",
    "social.signin.gate.action": "Inloggen",
    "social.about.public.label": "Bereikbaar voor 'iedereen'-berichten",
    // Rework pass 2 (overrides #2/#3): the original wording claimed "no discovery surface
    // exists anywhere in this design" -- false now that AddFriendSheet's search exists. Revised
    // to cross-reference the discoverable toggle instead of denying discoverability outright.
    "social.about.public.hint":
      "Zet dit aan zodat vrienden buiten je groepen je kunnen bereiken met een bericht aan 'iedereen' in een proeverij. Dit regelt alleen wie je broadcasts kan sturen, niet of je vindbaar bent in zoekresultaten — dat regel je bij 'Anderen kunnen je vinden'.",
    "social.about.discoverable.label": "Anderen kunnen je vinden",
    "social.about.discoverable.hint":
      "Je gebruikersnaam en naam zijn doorzoekbaar voor andere gebruikers. Zet dit uit om onvindbaar te zijn.",

    // SignIn
    "social.signin.title": "Inloggen",
    "social.signin.subtitle": "Kies hoe je wilt inloggen",
    "social.signin.provider.google": "Google",
    "social.signin.provider.microsoft": "Microsoft",
    "social.signin.provider.instagram": "Instagram",
    "social.signin.provider.tiktok": "TikTok",
    "social.signin.provider.pending": "Nog niet beschikbaar — wordt beoordeeld door de aanbieder",
    "social.signin.error.denied": "Inloggen is geannuleerd of geweigerd.",
    "social.signin.error.generic": "Inloggen is niet gelukt. Probeer het opnieuw.",
    "social.signin.success.title": "Ingelogd",

    // Friends
    "social.friends.title": "Vrienden",
    "social.friends.empty.title": "nog geen vrienden",
    "social.friends.empty.body": "Voeg een vriend toe om samen te proeven.",
    "social.friends.empty.action": "Vriend toevoegen",
    "social.friends.incoming.title": "Verzoeken",
    "social.friends.incoming.accept": "Accepteren",
    "social.friends.incoming.decline": "Weigeren",
    "social.friends.outgoing.label": "Verzoek verstuurd",
    "social.friends.outgoing.cancel": "Verzoek intrekken",
    "social.friends.row.block": "Blokkeren",
    "social.friends.row.unblock": "Deblokkeren",
    "social.friends.row.remove": "Verwijderen",
    "social.friends.accepted.toast": "Jullie zijn nu vrienden",

    // AddFriendSheet (rework pass 2, overrides #2/#3): real discoverable substring search,
    // no longer "handle/email" only.
    "social.friends.add.title": "Vriend toevoegen",
    "social.friends.add.searchPlaceholder": "Naam of gebruikersnaam",
    "social.friends.add.send": "Verzoek versturen",
    "social.friends.add.queued": "Wordt verstuurd zodra je weer online bent",
    "social.friends.add.sent": "Verzonden",
    "social.friends.add.noResults": "Geen resultaten",
    "social.friends.add.action": "Toevoegen",
    "social.friends.search.resultCount": "{n} resultaten",

    // Groups
    "social.groups.title": "Groepen",
    "social.groups.empty.title": "nog geen groepen",
    "social.groups.empty.body": "Maak een groep om samen te proeven.",
    "social.groups.empty.action": "Groep aanmaken",
    "social.groups.memberCount": "{n} leden",
    "social.groups.role.owner": "eigenaar",
    "social.groups.role.admin": "beheerder",
    "social.groups.role.member": "lid",

    // CreateGroupSheet / edit
    "social.groups.create.title": "Groep aanmaken",
    "social.groups.create.name": "Naam",
    "social.groups.create.description": "Omschrijving (optioneel)",
    "social.groups.create.confirm": "Aanmaken",
    "social.groups.edit.title": "Groep bewerken",
    "social.groups.edit.confirm": "Opslaan",

    "social.groups.avatar.upload": "Foto kiezen",
    "social.groups.avatar.uploading": "Bezig met uploaden…",
    "social.groups.avatar.remove": "Foto verwijderen",

    "social.groups.visibility.label": "Wie kan deze groep zien",
    "social.groups.visibility.membersAndInvitees": "Zichtbaar voor leden en genodigden",
    "social.groups.visibility.membersOnly": "Alleen leden, geen linkvoorbeeld",
    // Human override #3: the sole opt-in exception to the no-public-directory guardrail.
    "social.groups.visibility.discoverable": "Zichtbaar in het groepenoverzicht",

    // GroupDirectory / GroupDirectoryPreview (new, override #3)
    "social.groups.directory.entry": "Zoek een groep",
    "social.groups.directory.title": "Zoek een groep",
    "social.groups.directory.searchPlaceholder": "Naam van de groep",
    "social.groups.directory.noResults": "Geen groepen gevonden",
    "social.groups.directory.resultCount": "{n} resultaten",
    "social.groups.directory.requestToJoin": "Lidmaatschap aanvragen",
    "social.groups.directory.requested": "Aangevraagd",

    // GroupDetail's join-requests sub-section (discoverable groups only, owner/admin view)
    "social.groups.joinRequests.title": "Aanvragen",
    "social.groups.joinRequests.accept": "Accepteren",
    "social.groups.joinRequests.decline": "Weigeren",

    "social.groups.invitePermission.label": "Wie mag uitnodigen",
    "social.groups.invitePermission.ownerAdmin": "Alleen eigenaar en beheerders",
    "social.groups.invitePermission.anyMember": "Elk lid",

    // GroupDetail
    "social.groups.detail.editAction": "Groep bewerken",
    "social.groups.detail.removeMember": "Verwijderen",
    "social.groups.detail.leave": "Groep verlaten",
    "social.groups.detail.delete": "Groep verwijderen",
    "social.groups.detail.loadMore": "Meer tonen",
    "social.groups.detail.zeroOwnerLeaveBlocked":
      "Draag eerst het eigenaarschap over, of verwijder de groep",

    // GroupDetail's invite button; SessionInvite's empty friend picker
    "social.groups.invite.title": "Uitnodigen",
    "social.groups.invite.noFriends": "Geen vrienden om uit te nodigen.",

    // SessionDetail
    "social.session.title": "Proeverij",
    "social.session.draft.nameLabel": "Naam",
    "social.session.draft.invite": "Uitnodigen",
    "social.session.draft.startNow": "Nu starten",
    "social.session.scheduled.label": "Gepland voor {date}",
    "social.session.action.schedule": "Inplannen",
    "social.session.action.start": "Starten",
    "social.session.action.cancel": "Annuleren",
    "social.session.action.end": "Beëindigen",
    "social.session.status.invited": "uitgenodigd",
    "social.session.status.joined": "meegedaan",
    "social.session.status.online": "online",
    "social.session.status.offline": "offline",
    "social.session.status.live": "Live",
    "social.session.ended.title": "Beëindigd",
    "social.session.ended.toast": "deze proeverij is beëindigd",
    "social.session.reconnecting": "Opnieuw verbinden…",
    "social.session.presenceCount": "{n} online",

    "social.session.schedule.date": "Datum (optioneel)",
    "social.session.schedule.time": "Tijd (optioneel)",
    "social.session.schedule.invalid": "Kies een datum en tijd in de toekomst.",

    // SessionInvite
    "social.session.invite.title": "Deelnemers uitnodigen",
    "social.session.invite.confirm": "Uitnodigen",
    "social.session.invite.sourceFriends": "Vrienden",
    "social.session.invite.sourceGroup": "Groep",

    // BroadcastComposer
    "social.broadcast.composer.title": "Bericht sturen",
    "social.broadcast.composer.targetFriends": "Vrienden",
    "social.broadcast.composer.targetGroup": "Groep",
    "social.broadcast.composer.targetEveryone": "Iedereen in deze proeverij",
    "social.broadcast.composer.textPlaceholder": "Je bericht…",
    "social.broadcast.composer.send": "Versturen",
    "social.broadcast.composer.queued": "Wordt verstuurd zodra je weer online bent",
    "social.broadcast.composer.sent": "Verstuurd",
    "social.broadcast.composer.sentPartial": "Verstuurd, {n} niet afgeleverd",

    "social.broadcast.panel.title": "Aan jou gericht in deze proeverij",
    "social.broadcast.panel.empty": "Nog niets aan jou gericht in deze proeverij",

    // SharePreview
    "social.share.title": "Delen",
    "social.share.action": "Delen",
    "social.share.shared": "Gedeeld",
    "social.share.copied": "Link gekopieerd",

    // Shared row labels (UserCard/Avatar across screens)
    "social.presence.online": "online",
    "social.presence.offline": "offline",

    // ShareInviteSheet: a personal friend link or a group link, shared through the user's own apps.
    "social.invite.friends.action": "Uitnodigen",
    "social.invite.friends.title": "Vrienden uitnodigen",
    "social.invite.friends.lead":
      "Stuur je persoonlijke link. Wie hem opent en inlogt, wordt je vriend.",
    "social.invite.group.title": "Uitnodigen voor {group}",
    "social.invite.group.lead": "Wie de link opent of de code intypt, komt in de groep.",
    "social.invite.channel.share": "Delen…",
    "social.invite.channel.whatsapp": "WhatsApp",
    "social.invite.channel.email": "E-mail",
    "social.invite.channel.sms": "Sms",
    "social.invite.channel.copy": "Link kopiëren",
    "social.invite.channel.copied": "Link gekopieerd",
    "social.invite.qr.label": "QR-code met de uitnodiging",
    "social.invite.code.label": "Of zeg deze code",
    "social.invite.code.until": "Geldig tot {time}",
    "social.invite.status": "Nog {left} van {max} keer te gebruiken, geldig tot {date}",
    "social.invite.audience.one": "Eén persoon",
    "social.invite.audience.many": "Tot 10 mensen",
    "social.invite.audience.manyHint": "Bij meer mensen bevestig jij elk verzoek.",
    "social.invite.approval.label": "Nieuwe leden eerst goedkeuren",
    "social.invite.joined.title": "Wie deed mee",
    "social.invite.revoke": "Link intrekken",
    "social.invite.revoked": "Deze link werkt niet meer.",
    "social.invite.renew": "Nieuwe link maken",

    // InviteLanding: what an invitee sees after opening a link or typing a code.
    "social.invite.landing.title": "Uitnodiging",
    "social.invite.landing.friend": "{name} nodigt je uit om samen te proeven.",
    "social.invite.landing.group": "{name} nodigt je uit voor {group}.",
    "social.invite.landing.groupHidden": "{name} nodigt je uit voor een groep.",
    "social.invite.landing.approval": "Een beheerder keurt je aanvraag goed.",
    "social.invite.landing.expires": "Geldig tot {date}",
    "social.invite.landing.accept": "Accepteren",
    "social.invite.landing.decline": "Nee, bedankt",
    "social.invite.landing.signIn": "Inloggen om te accepteren",
    "social.invite.landing.gone.title": "Deze uitnodiging werkt niet meer",
    "social.invite.landing.gone.body": "Vraag om een nieuwe link of code.",
    "social.invite.landing.none.title": "Geen uitnodiging gevonden",
    "social.invite.landing.none.body": "Open de link opnieuw, of typ de code.",
    "social.invite.outcome.friends": "Jullie zijn nu vrienden",
    "social.invite.outcome.requested": "Verzoek verstuurd. {name} bevestigt het.",
    "social.invite.outcome.joined": "Je bent lid van de groep",
    "social.invite.outcome.joinRequested": "Aanvraag verstuurd. Een beheerder keurt hem goed.",
    "social.invite.outcome.toFriends": "Naar vrienden",
    "social.invite.outcome.toGroup": "Naar de groep",

    // CodeEntrySheet: "Have a code?" takes three words (an invite) or six (a flight).
    "social.code.action": "Heb je een code?",
    "social.code.title": "Code invoeren",
    "social.code.hint": "Drie woorden voor een uitnodiging, zes voor een proeverij.",
    "social.code.placeholder": "bijv. leaf lime loft",
    "social.code.submit": "Verder",
    "social.code.invalid": "Dat is geen code. Kijk de woorden nog eens na.",

    // Samen: the social tab, its inbox, the profile others see and the settings drawer (Phase 2).
    "social.samen.title": "Samen",
    "social.samen.badge": "{label}, {count} nieuw",
    "social.samen.announce": "{count} nieuw in Samen",
    "social.samen.profile": "Je profiel",
    "social.samen.lead": "Je vrienden, je groepen en wat er op je wacht.",

    "social.inbox.title": "Inbox",
    "social.inbox.tab.received": "Ontvangen",
    "social.inbox.tab.sent": "Verstuurd",
    "social.inbox.tab.updates": "Updates",
    "social.inbox.empty.received": "Er wacht niets op je.",
    "social.inbox.empty.sent": "Je wacht op niemand.",
    "social.inbox.empty.updates": "Nog geen nieuws.",
    "social.inbox.friendRequest": "wil vrienden worden",
    "social.inbox.groupInvite": "{name} nodigt je uit voor {group}",
    "social.inbox.groupInviteAnonymous": "Uitnodiging voor {group}",
    "social.inbox.joinRequest": "wil lid worden van {group}",
    "social.inbox.sessionInvite": "{name} nodigt je uit voor {session}",
    "social.inbox.accept": "Accepteren",
    "social.inbox.decline": "Weigeren",
    "social.inbox.open": "Bekijken",
    "social.inbox.cancel": "Intrekken",
    "social.inbox.remind": "Herinneren",
    "social.inbox.reminded": "Herinnerd",
    "social.inbox.sent.friendRequest": "Vriendschapsverzoek",
    "social.inbox.sent.groupInvite": "Uitgenodigd voor {group}",
    "social.inbox.links.title": "Je links",
    "social.inbox.link.friend": "Persoonlijke link",
    "social.inbox.link.group": "Link voor {group}",
    "social.inbox.link.ended": "Werkt niet meer",
    "social.inbox.link.joined": "Deden mee: {names}",
    "social.inbox.link.reshare": "Opnieuw delen",
    "social.inbox.update.friendAccepted": "{name} is nu je vriend",
    "social.inbox.update.groupInviteAccepted": "{name} is lid geworden van {group}",
    "social.inbox.update.joinApproved": "Je bent toegelaten tot {group}",
    "social.inbox.someone": "Iemand",
    "social.inbox.aGroup": "een groep",
    "social.inbox.queued": "Gaat door zodra je weer online bent",

    "social.profile.title": "Profiel",
    "social.profile.lead": "Zo zien anderen je.",
    "social.profile.noGroups": "Je zit nog in geen enkele groep.",
    "social.profile.signedOut": "Log in voor een profiel dat je vrienden zien.",
    "social.settings.title": "Instellingen",
    "social.settings.open": "Instellingen openen",

    "social.notify.title": "Meldingen",
    "social.notify.invites": "Uitnodigingen en verzoeken",
    "social.notify.invites.hint":
      "Een melding als iemand je uitnodigt of vriend wil worden. Wie accepteert, zie je in je inbox.",
    "social.notify.quiet": "Stille uren",
    "social.notify.quiet.from": "Van",
    "social.notify.quiet.until": "Tot",
    "social.notify.quiet.hint":
      "In deze uren geen meldingen; alles staat wel in je inbox. Hoogstens {n} meldingen per dag.",
    "social.notify.mutes.title": "Gedempt",
    "social.notify.mutes.empty":
      "Niemand gedempt. Een gedempte vriend of groep stuurt je geen meldingen meer.",
    "social.notify.mute": "Dempen",
    "social.notify.unmute": "Niet meer dempen",

    "social.groups.inviteFriends": "Vrienden uitnodigen",
    "social.groups.inviteFriends.sent": "{n} uitgenodigd. Ze komen erbij zodra ze accepteren.",
    "social.groups.inviteLink": "Link delen",
    "social.groups.log.title": "Logboek",
    "social.groups.log.empty": "Nog niets gebeurd.",
    "social.groups.log.created": "{name} maakte de groep",
    "social.groups.log.joined": "{name} is lid geworden",
    "social.groups.log.left": "{name} heeft de groep verlaten",
    "social.groups.log.removed": "{name} is uit de groep gehaald",
    "social.groups.log.role": "{name} is nu {role}",
    "social.groups.mute": "Groep dempen",
    "social.groups.unmute": "Groep niet meer dempen",
  },
  en: {
    "social.about.section.account": "Account",
    "social.about.sync.title": "Keep your notes with you",
    "social.about.sync.action": "Sign in",
    "social.about.sync.manage": "Manage",
    "social.about.sync.signedInAs": "Signed in as {email}",
    "social.about.sync.unconfirmed":
      "We couldn't check your account just now; you stay signed in and we'll try again shortly.",
    "social.signin.gate.title": "Sign in to taste together",
    "social.signin.gate.body":
      "Friends, groups and tastings belong to an account. The notes on this device stay yours.",
    "social.signin.gate.action": "Sign in",
    "social.about.public.label": "Reachable for 'everyone' broadcasts",
    "social.about.public.hint":
      "Turn this on so friends outside your groups can reach you with an 'everyone' message in a tasting. This only controls who can send you broadcasts, not whether you're findable in search results — that's controlled separately under 'Others can find you'.",
    "social.about.discoverable.label": "Others can find you",
    "social.about.discoverable.hint":
      "Your handle and name are searchable by other users. Turn this off to be unfindable.",

    "social.signin.title": "Sign in",
    "social.signin.subtitle": "Choose how to sign in",
    "social.signin.provider.google": "Google",
    "social.signin.provider.microsoft": "Microsoft",
    "social.signin.provider.instagram": "Instagram",
    "social.signin.provider.tiktok": "TikTok",
    "social.signin.provider.pending": "Not yet available — pending provider review",
    "social.signin.error.denied": "Sign-in was cancelled or denied.",
    "social.signin.error.generic": "Sign-in failed. Please try again.",
    "social.signin.success.title": "Signed in",

    "social.friends.title": "Friends",
    "social.friends.empty.title": "no friends yet",
    "social.friends.empty.body": "Add a friend to taste together.",
    "social.friends.empty.action": "Add friend",
    "social.friends.incoming.title": "Requests",
    "social.friends.incoming.accept": "Accept",
    "social.friends.incoming.decline": "Decline",
    "social.friends.outgoing.label": "Request sent",
    "social.friends.outgoing.cancel": "Cancel request",
    "social.friends.row.block": "Block",
    "social.friends.row.unblock": "Unblock",
    "social.friends.row.remove": "Remove",
    "social.friends.accepted.toast": "You're friends now",

    "social.friends.add.title": "Add friend",
    "social.friends.add.searchPlaceholder": "Name or handle",
    "social.friends.add.send": "Send request",
    "social.friends.add.queued": "Sends once you're back online",
    "social.friends.add.sent": "Sent",
    "social.friends.add.noResults": "No results",
    "social.friends.add.action": "Add",
    "social.friends.search.resultCount": "{n} results",

    "social.groups.title": "Groups",
    "social.groups.empty.title": "no groups yet",
    "social.groups.empty.body": "Create a group to taste together.",
    "social.groups.empty.action": "Create group",
    "social.groups.memberCount": "{n} members",
    "social.groups.role.owner": "owner",
    "social.groups.role.admin": "admin",
    "social.groups.role.member": "member",

    "social.groups.create.title": "Create group",
    "social.groups.create.name": "Name",
    "social.groups.create.description": "Description (optional)",
    "social.groups.create.confirm": "Create",
    "social.groups.edit.title": "Edit group",
    "social.groups.edit.confirm": "Save",

    "social.groups.avatar.upload": "Choose photo",
    "social.groups.avatar.uploading": "Uploading…",
    "social.groups.avatar.remove": "Remove photo",

    "social.groups.visibility.label": "Who can view this group",
    "social.groups.visibility.membersAndInvitees": "Visible to members and invitees",
    "social.groups.visibility.membersOnly": "Members only, no link preview",
    "social.groups.visibility.discoverable": "Listed in the group directory",

    "social.groups.directory.entry": "Find a group",
    "social.groups.directory.title": "Find a group",
    "social.groups.directory.searchPlaceholder": "Group name",
    "social.groups.directory.noResults": "No groups found",
    "social.groups.directory.resultCount": "{n} results",
    "social.groups.directory.requestToJoin": "Request to join",
    "social.groups.directory.requested": "Requested",

    "social.groups.joinRequests.title": "Join requests",
    "social.groups.joinRequests.accept": "Accept",
    "social.groups.joinRequests.decline": "Decline",

    "social.groups.invitePermission.label": "Who can invite",
    "social.groups.invitePermission.ownerAdmin": "Only owner and admins",
    "social.groups.invitePermission.anyMember": "Any member",

    "social.groups.detail.editAction": "Edit group",
    "social.groups.detail.removeMember": "Remove",
    "social.groups.detail.leave": "Leave group",
    "social.groups.detail.delete": "Delete group",
    "social.groups.detail.loadMore": "Show more",
    "social.groups.detail.zeroOwnerLeaveBlocked": "Transfer ownership first, or delete the group",

    "social.groups.invite.title": "Invite",
    "social.groups.invite.noFriends": "No friends to invite.",

    "social.session.title": "Tasting",
    "social.session.draft.nameLabel": "Name",
    "social.session.draft.invite": "Invite",
    "social.session.draft.startNow": "Start now",
    "social.session.scheduled.label": "Scheduled for {date}",
    "social.session.action.schedule": "Schedule",
    "social.session.action.start": "Start",
    "social.session.action.cancel": "Cancel",
    "social.session.action.end": "End",
    "social.session.status.invited": "invited",
    "social.session.status.joined": "joined",
    "social.session.status.online": "online",
    "social.session.status.offline": "offline",
    "social.session.status.live": "Live",
    "social.session.ended.title": "Ended",
    "social.session.ended.toast": "this tasting has ended",
    "social.session.reconnecting": "Reconnecting…",
    "social.session.presenceCount": "{n} online",

    "social.session.schedule.date": "Date (optional)",
    "social.session.schedule.time": "Time (optional)",
    "social.session.schedule.invalid": "Choose a date and time in the future.",

    "social.session.invite.title": "Invite participants",
    "social.session.invite.confirm": "Invite",
    "social.session.invite.sourceFriends": "Friends",
    "social.session.invite.sourceGroup": "Group",

    "social.broadcast.composer.title": "Send a message",
    "social.broadcast.composer.targetFriends": "Friends",
    "social.broadcast.composer.targetGroup": "Group",
    "social.broadcast.composer.targetEveryone": "Everyone in this tasting",
    "social.broadcast.composer.textPlaceholder": "Your message…",
    "social.broadcast.composer.send": "Send",
    "social.broadcast.composer.queued": "Sends once you're back online",
    "social.broadcast.composer.sent": "Sent",
    "social.broadcast.composer.sentPartial": "Sent, {n} undelivered",

    "social.broadcast.panel.title": "Targeted at you in this tasting",
    "social.broadcast.panel.empty": "Nothing targeted at you in this tasting yet",

    "social.share.title": "Share",
    "social.share.action": "Share",
    "social.share.shared": "Shared",
    "social.share.copied": "Link copied",

    "social.presence.online": "online",
    "social.presence.offline": "offline",

    "social.invite.friends.action": "Invite",
    "social.invite.friends.title": "Invite friends",
    "social.invite.friends.lead":
      "Send your personal link. Whoever opens it and signs in becomes your friend.",
    "social.invite.group.title": "Invite to {group}",
    "social.invite.group.lead": "Anyone who opens the link or types the code joins the group.",
    "social.invite.channel.share": "Share…",
    "social.invite.channel.whatsapp": "WhatsApp",
    "social.invite.channel.email": "Email",
    "social.invite.channel.sms": "Text message",
    "social.invite.channel.copy": "Copy link",
    "social.invite.channel.copied": "Link copied",
    "social.invite.qr.label": "QR code with the invite",
    "social.invite.code.label": "Or say this code",
    "social.invite.code.until": "Valid until {time}",
    "social.invite.status": "{left} of {max} uses left, valid until {date}",
    "social.invite.audience.one": "One person",
    "social.invite.audience.many": "Up to 10 people",
    "social.invite.audience.manyHint": "With more people, you confirm each request.",
    "social.invite.approval.label": "Approve new members first",
    "social.invite.joined.title": "Who joined",
    "social.invite.revoke": "Revoke link",
    "social.invite.revoked": "This link no longer works.",
    "social.invite.renew": "Make a new link",

    "social.invite.landing.title": "Invitation",
    "social.invite.landing.friend": "{name} invites you to taste together.",
    "social.invite.landing.group": "{name} invites you to join {group}.",
    "social.invite.landing.groupHidden": "{name} invites you to join a group.",
    "social.invite.landing.approval": "An admin approves your request.",
    "social.invite.landing.expires": "Valid until {date}",
    "social.invite.landing.accept": "Accept",
    "social.invite.landing.decline": "No thanks",
    "social.invite.landing.signIn": "Sign in to accept",
    "social.invite.landing.gone.title": "This invite no longer works",
    "social.invite.landing.gone.body": "Ask for a new link or code.",
    "social.invite.landing.none.title": "No invite found",
    "social.invite.landing.none.body": "Open the link again, or type the code.",
    "social.invite.outcome.friends": "You're friends now",
    "social.invite.outcome.requested": "Request sent. {name} will confirm it.",
    "social.invite.outcome.joined": "You've joined the group",
    "social.invite.outcome.joinRequested": "Request sent. An admin will approve it.",
    "social.invite.outcome.toFriends": "Go to friends",
    "social.invite.outcome.toGroup": "Go to the group",

    "social.code.action": "Have a code?",
    "social.code.title": "Enter a code",
    "social.code.hint": "Three words for an invite, six for a flight.",
    "social.code.placeholder": "e.g. leaf lime loft",
    "social.code.submit": "Continue",
    "social.code.invalid": "That isn't a code. Check the words again.",

    "social.samen.title": "Together",
    "social.samen.badge": "{label}, {count} new",
    "social.samen.announce": "{count} new in Together",
    "social.samen.profile": "Your profile",
    "social.samen.lead": "Your friends, your groups and what's waiting for you.",

    "social.inbox.title": "Inbox",
    "social.inbox.tab.received": "Received",
    "social.inbox.tab.sent": "Sent",
    "social.inbox.tab.updates": "Updates",
    "social.inbox.empty.received": "Nothing is waiting for you.",
    "social.inbox.empty.sent": "You're not waiting on anyone.",
    "social.inbox.empty.updates": "No news yet.",
    "social.inbox.friendRequest": "wants to be friends",
    "social.inbox.groupInvite": "{name} invites you to {group}",
    "social.inbox.groupInviteAnonymous": "Invitation to {group}",
    "social.inbox.joinRequest": "wants to join {group}",
    "social.inbox.sessionInvite": "{name} invites you to {session}",
    "social.inbox.accept": "Accept",
    "social.inbox.decline": "Decline",
    "social.inbox.open": "View",
    "social.inbox.cancel": "Withdraw",
    "social.inbox.remind": "Remind",
    "social.inbox.reminded": "Reminded",
    "social.inbox.sent.friendRequest": "Friend request",
    "social.inbox.sent.groupInvite": "Invited to {group}",
    "social.inbox.links.title": "Your links",
    "social.inbox.link.friend": "Personal link",
    "social.inbox.link.group": "Link to {group}",
    "social.inbox.link.ended": "No longer works",
    "social.inbox.link.joined": "Joined: {names}",
    "social.inbox.link.reshare": "Share again",
    "social.inbox.update.friendAccepted": "{name} is now your friend",
    "social.inbox.update.groupInviteAccepted": "{name} joined {group}",
    "social.inbox.update.joinApproved": "You're in {group}",
    "social.inbox.someone": "Someone",
    "social.inbox.aGroup": "a group",
    "social.inbox.queued": "Goes through once you're back online",

    "social.profile.title": "Profile",
    "social.profile.lead": "This is how others see you.",
    "social.profile.noGroups": "You're not in any group yet.",
    "social.profile.signedOut": "Sign in for a profile your friends can see.",
    "social.settings.title": "Settings",
    "social.settings.open": "Open settings",

    "social.notify.title": "Notifications",
    "social.notify.invites": "Invites and requests",
    "social.notify.invites.hint":
      "A notification when someone invites you or wants to be friends. Who accepted shows in your inbox.",
    "social.notify.quiet": "Quiet hours",
    "social.notify.quiet.from": "From",
    "social.notify.quiet.until": "Until",
    "social.notify.quiet.hint":
      "No notifications in these hours; everything still lands in your inbox. At most {n} notifications a day.",
    "social.notify.mutes.title": "Muted",
    "social.notify.mutes.empty":
      "Nobody muted. A muted friend or group no longer sends you notifications.",
    "social.notify.mute": "Mute",
    "social.notify.unmute": "Unmute",

    "social.groups.inviteFriends": "Invite friends",
    "social.groups.inviteFriends.sent": "{n} invited. They join once they accept.",
    "social.groups.inviteLink": "Share a link",
    "social.groups.log.title": "Log",
    "social.groups.log.empty": "Nothing has happened yet.",
    "social.groups.log.created": "{name} created the group",
    "social.groups.log.joined": "{name} joined",
    "social.groups.log.left": "{name} left the group",
    "social.groups.log.removed": "{name} was removed from the group",
    "social.groups.log.role": "{name} is now {role}",
    "social.groups.mute": "Mute group",
    "social.groups.unmute": "Unmute group",
  },
} as const;
